import React, { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  FaPlus,
  FaEdit,
  FaSearch,
  FaFilter,
  FaExternalLinkAlt,
  FaChair,
  FaVideo,
  FaUsers,
  FaSyncAlt,
  FaExclamationTriangle
} from "react-icons/fa";
import { fetchWithAuth } from "../../../utils/api";
import { formatDisplay12Hour } from "../../../utils/eventTimeUtils";
import CreateEventModal from "./CreateEventModal";
import "./AllEvents.css";

const AllEvents = () => {
  const [searchParams] = useSearchParams();

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Filters & Pagination
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "all");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRows, setTotalRows] = useState(0);

  // Modals
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);

  // Capacity Modal State
  const [capacityModalEvent, setCapacityModalEvent] = useState(null);
  const [newCapacity, setNewCapacity] = useState("");
  const [capacityError, setCapacityError] = useState("");
  const [savingCapacity, setSavingCapacity] = useState(false);

  // Meeting Link Modal State
  const [meetingModalEvent, setMeetingModalEvent] = useState(null);
  const [editPlatform, setEditPlatform] = useState("Google Meet");
  const [editMeetingUrl, setEditMeetingUrl] = useState("");
  const [meetingError, setMeetingError] = useState("");
  const [savingMeeting, setSavingMeeting] = useState(false);

  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const params = new URLSearchParams();
      if (search.trim()) params.set("search", search.trim());
      if (statusFilter !== "all") params.set("status", statusFilter);
      params.set("page", page.toString());
      params.set("limit", "10");

      const res = await fetchWithAuth(`/admin/events?${params.toString()}`);
      const data = await res.json();

      if (res.ok && data.success) {
        setEvents(data.events || []);
        if (data.pagination) {
          setTotalPages(data.pagination.totalPages);
          setTotalRows(data.pagination.totalRows);
        }
      } else {
        setError(data.message || "Failed to load events list");
      }
    } catch (err) {
      console.error("Admin events load error:", err);
      setError("Network error connecting to events service");
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, page]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    fetchEvents();
  };

  // Toggle publish / draft status
  const handleToggleStatus = async (event) => {
    const nextStatus = event.status === "Published" ? "Draft" : "Published";
    try {
      const res = await fetchWithAuth(`/admin/events/${event._id}/status`, {
        method: "PATCH",
        body: JSON.stringify({ status: nextStatus })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        fetchEvents();
      } else {
        alert(data.message || "Failed to update status");
      }
    } catch (err) {
      alert("Network error updating status");
    }
  };

  // Capacity update handler
  const handleSaveCapacity = async (e) => {
    e.preventDefault();
    setCapacityError("");

    const seatsNum = parseInt(newCapacity, 10);
    if (isNaN(seatsNum) || seatsNum <= 0) {
      setCapacityError("Please enter a valid positive number for total seats");
      return;
    }

    if (seatsNum < (capacityModalEvent.bookedSeats || 0)) {
      setCapacityError(
        `Cannot reduce total seats below current confirmed bookings (${capacityModalEvent.bookedSeats || 0})`
      );
      return;
    }

    try {
      setSavingCapacity(true);
      const res = await fetchWithAuth(`/admin/events/${capacityModalEvent._id}/capacity`, {
        method: "PUT",
        body: JSON.stringify({ totalSeats: seatsNum })
      });
      const data = await res.json();

      if (res.ok && data.success) {
        setCapacityModalEvent(null);
        fetchEvents();
      } else {
        setCapacityError(data.message || "Failed to update capacity");
      }
    } catch (err) {
      setCapacityError("Network error saving capacity");
    } finally {
      setSavingCapacity(false);
    }
  };

  // Meeting Link update handler
  const handleSaveMeeting = async (e) => {
    e.preventDefault();
    setMeetingError("");

    if (!editMeetingUrl.trim()) {
      setMeetingError("Meeting URL is required");
      return;
    }

    try {
      setSavingMeeting(true);
      const res = await fetchWithAuth(`/admin/events/${meetingModalEvent._id}`, {
        method: "PUT",
        body: JSON.stringify({
          meetingPlatform: editPlatform,
          privateMeetingUrl: editMeetingUrl.trim()
        })
      });
      const data = await res.json();

      if (res.ok && data.success) {
        setMeetingModalEvent(null);
        fetchEvents();
      } else {
        setMeetingError(data.message || "Failed to update meeting details");
      }
    } catch (err) {
      setMeetingError("Network error saving meeting details");
    } finally {
      setSavingMeeting(false);
    }
  };

  return (
    <div className="all-events-container">
      {/* HEADER SECTION */}
      <div className="all-events-header">
        <div>
          <h2>All Events Management</h2>
          <p>Create, publish, edit capacity, update meeting links, and monitor sessions.</p>
        </div>

        <div className="header-actions">
          <button type="button" className="action-btn-secondary" onClick={fetchEvents} title="Refresh">
            <FaSyncAlt className={loading ? "spin-icon" : ""} />
            <span>Refresh</span>
          </button>
          <button type="button" className="action-btn-primary" onClick={() => setIsCreateModalOpen(true)}>
            <FaPlus />
            <span>Create Event</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="all-events-error">
          <FaExclamationTriangle />
          <span>{error}</span>
        </div>
      )}

      {/* FILTER & SEARCH BAR */}
      <div className="events-filter-bar">
        <form className="search-box" onSubmit={handleSearchSubmit}>
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search by title, slug, or category..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button type="submit">Search</button>
        </form>

        <div className="status-filter-group">
          <FaFilter className="filter-icon" />
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
          >
            <option value="all">All Statuses</option>
            <option value="Published">Published</option>
            <option value="Draft">Draft</option>
          </select>
        </div>
      </div>

      {/* EVENTS TABLE */}
      <div className="all-events-table-card">
        <div className="table-responsive">
          <table className="all-events-table">
            <thead>
              <tr>
                <th>Event Banner & Title</th>
                <th>Slug</th>
                <th>Date & Time (IST)</th>
                <th>Platform</th>
                <th>Seats (Booked / Total)</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="table-loading-td">
                    <div className="events-spinner" />
                    <span>Loading events...</span>
                  </td>
                </tr>
              ) : events.length === 0 ? (
                <tr>
                  <td colSpan={7} className="table-empty-td">
                    No events found matching your filter criteria.
                  </td>
                </tr>
              ) : (
                events.map((ev) => {
                  const remaining = Math.max(0, (ev.totalSeats || 0) - (ev.bookedSeats || 0));
                  const isFull = remaining === 0;

                  return (
                    <tr key={ev._id}>
                      <td>
                        <div className="event-info-cell">
                          <img src={ev.bannerImage} alt={ev.title} className="event-thumb-img" />
                          <div>
                            <span className="event-title-main">{ev.title}</span>
                            <span className="event-category-sub">{ev.category}</span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <code className="event-slug-code">{ev.slug}</code>
                      </td>
                      <td>
                        <div className="event-date-cell">
                          <span>{ev.eventDate}</span>
                          <span className="event-time-sub">
                            {formatDisplay12Hour(ev.startTime)} – {formatDisplay12Hour(ev.endTime)}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span className="platform-tag">
                          <FaVideo /> {ev.meetingPlatform || "Google Meet"}
                        </span>
                      </td>
                      <td>
                        <div className="seats-badge-cell">
                          <span className={`seats-badge ${isFull ? "full" : ""}`}>
                            {ev.bookedSeats || 0} / {ev.totalSeats}
                          </span>
                          <span className="seats-sub-text">
                            {isFull ? "0 Seats Left" : `${remaining} Remaining`}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span className={`event-status-pill ${ev.status?.toLowerCase()}`}>
                          {ev.status}
                        </span>
                      </td>
                      <td>
                        <div className="table-actions-cell">
                          {/* Public View Button */}
                          <a
                            href={`/events/eventdetail/${ev.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="action-icon-btn view"
                            title="View Public Details"
                          >
                            <FaExternalLinkAlt />
                          </a>

                          {/* Edit Details Button */}
                          <button
                            type="button"
                            className="action-icon-btn edit"
                            title="Edit Event Details"
                            onClick={() => setEditingEvent(ev)}
                          >
                            <FaEdit />
                          </button>

                          {/* Edit Capacity Button */}
                          <button
                            type="button"
                            className="action-icon-btn capacity"
                            title="Edit Seat Capacity"
                            onClick={() => {
                              setCapacityModalEvent(ev);
                              setNewCapacity(ev.totalSeats.toString());
                              setCapacityError("");
                            }}
                          >
                            <FaChair />
                          </button>

                          {/* Edit Meeting Link */}
                          <button
                            type="button"
                            className="action-icon-btn meeting"
                            title="Edit Meeting Platform & Link"
                            onClick={() => {
                              setMeetingModalEvent(ev);
                              setEditPlatform(ev.meetingPlatform || "Google Meet");
                              setEditMeetingUrl(ev.privateMeetingUrl || "");
                              setMeetingError("");
                            }}
                          >
                            <FaVideo />
                          </button>

                          {/* Manage Registrations */}
                          <Link
                            to={`/admin/events/registrations?eventId=${ev._id}`}
                            className="action-icon-btn registrations"
                            title="Manage Registrations"
                          >
                            <FaUsers />
                          </Link>

                          {/* Toggle Status */}
                          <button
                            type="button"
                            className={`action-pill-btn ${ev.status === "Published" ? "unpublish" : "publish"}`}
                            onClick={() => handleToggleStatus(ev)}
                            title={ev.status === "Published" ? "Unpublish Event" : "Publish Event"}
                          >
                            {ev.status === "Published" ? "Unpublish" : "Publish"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* PAGINATION BAR */}
        {totalPages > 1 && (
          <div className="table-pagination-row">
            <span>
              Showing {events.length} of {totalRows} total events
            </span>
            <div className="pagination-buttons">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </button>
              <span>
                Page {page} of {totalPages}
              </span>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* CREATE / EDIT EVENT MODAL */}
      <CreateEventModal
        isOpen={isCreateModalOpen || !!editingEvent}
        eventToEdit={editingEvent}
        onClose={() => {
          setIsCreateModalOpen(false);
          setEditingEvent(null);
        }}
        onEventCreated={() => {
          fetchEvents();
        }}
        onEventUpdated={() => {
          fetchEvents();
        }}
      />

      {/* EDIT SEAT CAPACITY DIALOG */}
      {capacityModalEvent && (
        <div className="dialog-overlay" onClick={() => setCapacityModalEvent(null)}>
          <div className="dialog-card" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-header">
              <h3>Edit Seat Capacity</h3>
              <button
                type="button"
                className="dialog-close"
                onClick={() => setCapacityModalEvent(null)}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSaveCapacity}>
              <div className="dialog-body">
                <p className="dialog-event-name">{capacityModalEvent.title}</p>

                <div className="capacity-meta-box">
                  <div className="meta-box-row">
                    <span>Current Total Seats:</span>
                    <b>{capacityModalEvent.totalSeats}</b>
                  </div>
                  <div className="meta-box-row">
                    <span>Confirmed Bookings:</span>
                    <b className="highlight-booked">{capacityModalEvent.bookedSeats || 0}</b>
                  </div>
                  <div className="meta-box-row">
                    <span>Remaining Seats:</span>
                    <b>{Math.max(0, capacityModalEvent.totalSeats - (capacityModalEvent.bookedSeats || 0))}</b>
                  </div>
                </div>

                <div className="input-group">
                  <label className="input-label">New Total Seats *</label>
                  <input
                    type="number"
                    min={capacityModalEvent.bookedSeats || 1}
                    className="modal-input"
                    value={newCapacity}
                    onChange={(e) => setNewCapacity(e.target.value)}
                    required
                  />
                  <span className="field-hint">
                    Rule: Cannot be decreased below current confirmed bookings ({capacityModalEvent.bookedSeats || 0}).
                  </span>
                </div>

                {capacityError && <div className="dialog-error">{capacityError}</div>}
              </div>

              <div className="dialog-footer">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => setCapacityModalEvent(null)}
                  disabled={savingCapacity}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-save" disabled={savingCapacity}>
                  {savingCapacity ? "Saving..." : "Update Capacity"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* EDIT MEETING LINK DIALOG */}
      {meetingModalEvent && (
        <div className="dialog-overlay" onClick={() => setMeetingModalEvent(null)}>
          <div className="dialog-card" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-header">
              <h3>Edit Meeting Link & Platform</h3>
              <button
                type="button"
                className="dialog-close"
                onClick={() => setMeetingModalEvent(null)}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSaveMeeting}>
              <div className="dialog-body">
                <p className="dialog-event-name">{meetingModalEvent.title}</p>

                <div className="input-group">
                  <label className="input-label">Platform *</label>
                  <select
                    className="modal-select"
                    value={editPlatform}
                    onChange={(e) => setEditPlatform(e.target.value)}
                  >
                    <option value="Google Meet">Google Meet</option>
                    <option value="Zoom">Zoom</option>
                  </select>
                </div>

                <div className="input-group">
                  <label className="input-label">Private Joining URL *</label>
                  <input
                    type="url"
                    className="modal-input"
                    value={editMeetingUrl}
                    onChange={(e) => setEditMeetingUrl(e.target.value)}
                    required
                  />
                  <span className="field-hint">
                    Private joining link sent only in attendee confirmation emails.
                  </span>
                </div>

                {meetingError && <div className="dialog-error">{meetingError}</div>}
              </div>

              <div className="dialog-footer">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => setMeetingModalEvent(null)}
                  disabled={savingMeeting}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-save" disabled={savingMeeting}>
                  {savingMeeting ? "Saving..." : "Save Meeting Link"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllEvents;
